import React from 'react';
import { useParams } from 'react-router-dom';
import FormInput from '../../components/resume/FormInput';
import Toggle from '../../components/resume/Toggle';
import { useResume } from '../../context/ResumeContext';
import useAutosave from '../../hooks/useAutosave';
import SaveStatusChip from '../../components/resume/editor/SaveStatusChip';

const EMAIL_REGEX = /^\S+@\S+\.\S+$/;
const PHONE_REGEX = /^\+?[\d\s().-]{7,20}$/;

const Contact = () => {
    const { contact, setContact, contactToggles, setContactToggles } = useResume();
    const { resumeId } = useParams();

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setContact(prev => ({ ...prev, [name]: value }));
    };

    const handleToggle = (name) => {
        setContactToggles(prev => ({ ...prev, [name]: !prev[name] }));
    };

    const validateContact = () => {
        const { fullName, email, phone, linkedin, website, country, state, city } = contact;
        const allEmpty = !fullName && !email && !phone && !linkedin && !website && !country && !state && !city;
        if (allEmpty) return null;

        if (email && !EMAIL_REGEX.test(email)) return false;
        if (phone && !PHONE_REGEX.test(phone)) return false;
        if (!fullName && (email || phone || linkedin || website || country || state || city)) return false;

        return true;
    };

    const handleSave = async () => {
        if (!resumeId) throw new Error('No resume ID');

        const response = await fetch('https://renaisons.com/api/save_contact.php', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                resume_id: resumeId,
                ...contact
            }),
        });

        const result = await response.json();

        if (!response.ok || result.status !== 'success') {
            throw new Error(result.message || 'save failed');
        }
    };

    const { status, retry } = useAutosave({ sectionKey: 'contact', data: contact, save: handleSave, validate: validateContact });

    return (
        <div className="space-y-6">
            <div className="flex justify-end">
                <SaveStatusChip status={status} onRetry={retry} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <FormInput label="Full Name" name="fullName" value={contact.fullName} onChange={handleInputChange} placeholder="John Doe" />
                <div className="flex-1">
                    <div className="flex justify-between items-center mb-2">
                        <label htmlFor="email" className="block text-xs font-bold text-gray-400 uppercase">Email Address</label>
                    </div>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={contact.email}
                        onChange={handleInputChange}
                        placeholder="john.doe@example.com"
                        className="w-full bg-[#0f172a] border border-gray-600 rounded-md p-3 focus:ring-cyan-500/60 focus:border-cyan-500/60"
                    />
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="flex-1">
                    <div className="flex justify-between items-center mb-2">
                        <label htmlFor="phone" className="block text-xs font-bold text-gray-400 uppercase">Phone Number</label>
                        <Toggle label="Show on resume" checked={contactToggles.phone} onChange={() => handleToggle('phone')} />
                    </div>
                    <input
                        type="text"
                        id="phone"
                        name="phone"
                        value={contact.phone}
                        onChange={handleInputChange}
                        placeholder="(123) 456-7890"
                        className="w-full bg-[#0f172a] border border-gray-600 rounded-md p-3 focus:ring-cyan-500/60 focus:border-cyan-500/60"
                    />
                </div>
                <div className="flex-1">
                    <div className="flex justify-between items-center mb-2">
                        <label htmlFor="linkedin" className="block text-xs font-bold text-gray-400 uppercase mb-2">
                            LinkedIn URL
                        </label>
                        <Toggle label="Show on resume" checked={contactToggles.linkedin} onChange={() => handleToggle('linkedin')} />
                    </div>

                    <div className="flex items-center bg-[#0f172a] border border-gray-600 rounded-md focus-within:ring-2 focus-within:ring-cyan-500/60 focus-within:border-cyan-500/60">
                        <span className="pl-3 text-gray-500">linkedin.com/in/</span>
                        <input
                            type="text"
                            id="linkedin"
                            name="linkedin"
                            value={contact.linkedin}
                            onChange={handleInputChange}
                            placeholder="your-username"
                            className="flex-1 bg-transparent p-3 text-white border-none focus:ring-0"
                        />
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <FormInput label="Personal Website or Relevant Link" name="website" value={contact.website} onChange={handleInputChange} placeholder="https://www.johndoe.com" />
                <div className="flex-1">
                    <div className="flex justify-between items-center mb-2">
                        <label htmlFor="country" className="block text-xs font-bold text-gray-400 uppercase">Country</label>
                        <Toggle label="Show on resume" checked={contactToggles.country} onChange={() => handleToggle('country')} />
                    </div>
                    <input
                        type="text"
                        id="country"
                        name="country"
                        value={contact.country}
                        onChange={handleInputChange}
                        placeholder="United States"
                        className="w-full bg-[#0f172a] border border-gray-600 rounded-md p-3 focus:ring-cyan-500/60 focus:border-cyan-500/60"
                    />
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="flex-1">
                    <div className="flex justify-between items-center mb-2">
                        <label htmlFor="state" className="block text-xs font-bold text-gray-400 uppercase">State</label>
                        <Toggle label="Show on resume" checked={contactToggles.state} onChange={() => handleToggle('state')} />
                    </div>
                    <input
                        type="text"
                        id="state"
                        name="state"
                        value={contact.state}
                        onChange={handleInputChange}
                        placeholder="Texas"
                        className="w-full bg-[#0f172a] border border-gray-600 rounded-md p-3 focus:ring-cyan-500/60 focus:border-cyan-500/60"
                    />
                </div>
                <div className="flex-1">
                    <div className="flex justify-between items-center mb-2">
                        <label htmlFor="city" className="block text-xs font-bold text-gray-400 uppercase">City</label>
                        <Toggle label="Show on resume" checked={contactToggles.city} onChange={() => handleToggle('city')} />
                    </div>
                    <input
                        type="text"
                        id="city"
                        name="city"
                        value={contact.city}
                        onChange={handleInputChange}
                        placeholder="Dallas"
                        className="w-full bg-[#0f172a] border border-gray-600 rounded-md p-3 focus:ring-cyan-500/60 focus:border-cyan-500/60"
                    />
                </div>
            </div>
        </div>
    );
};

export default Contact;
